<script lang="ts" setup>
import EmptyContent from "@/components/mobile/common/EmptyContent.vue";
import {
  Popup,
  showConfirmDialog,
  showLoadingToast,
  showSuccessToast,
  ToastWrapperInstance,
} from "vant";
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useTraitAnimation } from "@/stores/traitAnimation";
import { TraitAnimationType } from "@/enums";
import i18n from "@/locales";

const router = useRouter();
const route = useRoute();
const traitId = ref(Number(route.query.traitId));
const loading = ref(false);
const traitAnimationStore = useTraitAnimation();
const showGuide = ref(false);
const animationList = computed(() => {
  return (
    traitAnimationStore.traitId2AnimationKeyList[traitId.value]?.map(
      (id) => traitAnimationStore.animationMap[id]
    ) ?? []
  );
});

// Lifecycle
onMounted(async () => {
  loading.value = true;
  try {
    await traitAnimationStore.getOrFetchTraitAnimationByTraitId(traitId.value);
  } finally {
    loading.value = false;
  }
});

const goCreateOrEdit = async (type: TraitAnimationType, id?: number) => {
  await router.push({
    name: "createAnimation",
    query: { type, traitId: traitId.value, id },
  });
};

const onDelete = async (id: number) => {
  let loading: ToastWrapperInstance | null = null;
  try {
    await showConfirmDialog({
      title: i18n.global.t("ConfirmModal.sureDeleteText"),
      message: i18n.global.t("ConfirmModal.deleteCurrentChatInfo"),
      confirmButtonText: i18n.global.t("Buttons.confirm"),
      cancelButtonText: i18n.global.t("Buttons.cancel"),
    });
    loading = showLoadingToast("");
    await traitAnimationStore.deleteTraitAnimation(id);
    loading.close();
    showSuccessToast(i18n.global.t("Message.deleteSuccessful"));
  } finally {
    loading?.close();
  }
};
</script>
<template>
  <div class="h-full grid grid-rows-[auto_1fr_auto]">
    <threads-nav show-back>
      <template #center> {{ $t("Animation.list") }}</template>
      <template #right>
        <button
          class="btn btn-primary btn-sm rounded"
          @click="showGuide = true"
        >
          {{ $t("Animation.btn.create") }}
        </button>
      </template>
    </threads-nav>
    <div class="w-full h-full overflow-hidden">
      <div
        v-if="loading"
        class="w-full h-full flex items-center justify-center"
      >
        <span class="loading"></span>
      </div>
      <empty-content
        v-else-if="animationList.length === 0"
        :desc="$t('Message.noAnimation')"
        :title="$t('Message.noContentAvailable')"
      />
      <div v-else class="w-full p-2 grid grid-cols-1 md:grid-cols-2 gap-2">
        <div
          v-for="item in animationList"
          :key="item!.id"
          class="flex flex-col rounded overflow-hidden bg-neutral shadow-md"
        >
          <div class="overflow-hidden bg-base-200">
            <base-img
              :media="item.portraitzz[0].portrait.media"
              :width="276"
              class="w-full"
              ratio="16/9"
            />
          </div>
          <div class="flex flex-col px-4 py-2">
            <div>{{ item.name }}</div>
            <div class="flex gap-1 justify-end">
              <button
                class="btn btn-sm rounded"
                @click="goCreateOrEdit(item.type, item.id)"
              >
                {{ $t("Buttons.edit") }}
              </button>
              <button class="btn btn-sm rounded" @click="onDelete(item.id)">
                {{ $t("Buttons.delete") }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <popup
      v-model:show="showGuide"
      class="w-[350px] py-10"
      position="center"
      round
      teleport="body"
    >
      <div
        class="flex flex-col items-center justify-center w-full h-full gap-4"
      >
        <div class="flex flex-col items-center justify-center gap-2">
          <i class="iconfont icon-movieend text-3xl text-base-content/50" />
          <div class="text-lg font-bold text-base-content/50">
            {{ $t("Animation.creation") }}
          </div>
        </div>
        <div class="flex flex-col gap-2">
          <button
            class="w-60 btn rounded btn-sm"
            @click="goCreateOrEdit(TraitAnimationType.Blink)"
          >
            {{ $t("Animation.blink") }}
          </button>
          <button
            :disabled="true"
            class="w-60 btn rounded btn-sm"
            @click="goCreateOrEdit(TraitAnimationType.LipSync)"
          >
            {{ $t("Animation.lipSync") }}
          </button>
        </div>
      </div>
    </popup>
  </div>
</template>
